import React from 'react';
import { Typography } from '../common/Typography';
import { getPlayerNameColor } from '../../utils/playerUtils';

/**
 * Normalizes upset_win roster from metadata (new shape with gender, or legacy name arrays).
 */
export function normalizeUpsetRoster(metadata, rosterKey, legacyNamesKey) {
  const roster = metadata?.[rosterKey];
  if (Array.isArray(roster) && roster.length > 0) {
    return roster.map((p) => ({
      first_name: p?.first_name || p?.firstName || '?',
      gender: p?.gender ?? null,
    }));
  }
  const legacy = metadata?.[legacyNamesKey];
  if (Array.isArray(legacy)) {
    return legacy.filter(Boolean).map((first_name) => ({ first_name, gender: null }));
  }
  return [];
}

function ColoredNameList({ players, separator = ' & ' }) {
  if (!players?.length) return null;
  return players.map((player, index) => (
    <React.Fragment key={`${player.first_name}-${index}`}>
      {index > 0 ? (
        <span className="text-text-secondary font-normal">{separator}</span>
      ) : null}
      <span className={getPlayerNameColor(player.gender)}>{player.first_name}</span>
    </React.Fragment>
  ));
}

/**
 * Second line for notable win (upset_win) moment cards: teammates vs opponents with avg team Elo.
 */
export function NotableWinSubline({ event, className = '' }) {
  if (event?.event_type !== 'upset_win') return null;

  const metadata = event.metadata || {};
  const teammates = normalizeUpsetRoster(metadata, 'teammates', 'teammate_first_names');
  const opponents = normalizeUpsetRoster(metadata, 'opponents', 'opponent_first_names');
  const teamElo = metadata.team_avg_elo ?? null;
  const oppElo = metadata.opponent_avg_elo ?? null;

  if (teamElo == null && oppElo == null && !teammates.length && !opponents.length) {
    return null;
  }

  const teamEloLabel = teamElo ?? '?';
  const oppEloLabel = oppElo ?? '?';

  return (
    <Typography
      variant="caption"
      className={`text-text-secondary leading-snug line-clamp-4 ${className}`.trim()}
    >
      {teammates.length > 0 ? (
        <>
          <span className="text-text-secondary font-normal">With </span>
          <ColoredNameList players={teammates} />
          <span className="text-text-secondary font-normal"> ({teamEloLabel})</span>
        </>
      ) : (
        <span className="text-text-secondary font-normal">You ({teamEloLabel})</span>
      )}
      {opponents.length > 0 ? (
        <>
          <span className="text-text-secondary font-normal"> vs </span>
          <ColoredNameList players={opponents} />
          <span className="text-text-secondary font-normal"> ({oppEloLabel})</span>
        </>
      ) : null}
    </Typography>
  );
}
